import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup"; // For form validation

import {
  EMPTY_STRING,
  subscriptionTabs,
  PLAN_TYPES_MAPPING,
  subscriptionConstants,
} from "../../../constants";
import { USER_ROLE } from "../../../system";
import { EditCountryIcon } from "../../../assets";
import { subscriptionActions } from "../../../redux/actions";
import MainSpinner from "../../../shared/components/spinner/MainSpinner";
import RoundedButton from "../../../shared/components/buttons/RoundedButton";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import ConfirmationPopup from "../../../shared/components/modals/ConfirmationPopup";

const { GET_SUBSCRIPTION_PLANS } = subscriptionConstants;

// Define schema for form validation using Yup
const schema = yup.object().shape({
  plans: yup.array().of(
    yup.object().shape({
      trialDays: yup
        .number()
        .integer("Value must be an integer")
        .required("Trial days are required")
        .min(0, "Trial days cannot be negative")
        .typeError("Invalid days"),
      freeListing: yup
        .number()
        .integer("Value must be an integer")
        .required("Free listings are required")
        .min(0, "Free listings cannot be negative")
        .typeError("Invalid value"),
      additionalListing: yup
        .number()
        .integer("Value must be an integer")
        .required("Additional listings are required")
        .min(0, "Additional listings cannot be negative")
        .typeError("Invalid value"),
      amount: yup
        .number()
        .required("Amount is required")
        .min(0, "Amount cannot be negative")
        .typeError("Invalid amount"), // Custom error message for non-number inputs
    })
  ),
});

const planHeadings = {
  2: "Beginner",
  3: "Professional",
  4: "Premium",
};

const PLAN_TYPE_MAPPING = {
  1: "/month",
  2: "/6 month",
  3: "/year",
};

const Subscription = ({
  spinnerActions,
  allSubscriptionPlans,
  getSubscriptionPlans,
  updateAllSubscriptionPlans,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentTab, setCurrentTab] = useState(subscriptionTabs.CUSTOMER);
  const [confirmationModal, setConfirmationModal] = useState(false);

  const openConfirmationModal = () => setConfirmationModal(true);
  const closeConfirmationModal = () => setConfirmationModal(false);

  const defaultPlans = allSubscriptionPlans.map((plan) => ({
    ...plan,
    trialDays: plan.trialDays ?? 0,
    freeListing: plan.freeListing ?? 0,
    additionalListing: plan.additionalListing ?? 0,
  }));

  const {
    watch,
    reset,
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: { plans: defaultPlans },
    resolver: yupResolver(schema), // Integrate Yup schema
  });
  // Fetch all subscription plans on component mount
  useEffect(() => {
    getSubscriptionPlans(USER_ROLE.SUPER_ADMIN);
  }, [getSubscriptionPlans]);

  // Update form values when subscription plans change
  useEffect(() => {
    setValue("plans", defaultPlans);
  }, [allSubscriptionPlans, setValue]);

  useEffect(() => {
    if (!isEditing) {
      reset({ plans: defaultPlans });
    }
  }, [isEditing]);

  // Group plans by their "plan" key for rendering
  const groupedPlans = watch("plans").reduce((acc, plan) => {
    if (!acc[plan.plan]) {
      acc[plan.plan] = [];
    }
    acc[plan.plan].push(plan);
    return acc;
  }, {});

  // Handle form submission
  const onSubmit = (data) => {
    openConfirmationModal();
  };

  // Render plans for each group
  const renderPlans = (plans) => {
    const planType = plans[0]?.plan;
    const planRole = plans[0]?.roleId;
    return (
      <div className="col-12 col-lg-5 col-xl-3 mt-5">
        {planRole !== USER_ROLE.USER && (
          <div className="plan-property plan-heading">
            {planHeadings[planType] || ""}
          </div>
        )}
        <div className="plan-property plan-property-heading mb-3">
          Plan charges in USD ($)
        </div>
        {plans.map((plan) => (
          <div
            className="d-flex justify-content-between mb-3"
            key={plan.planType}
          >
            <div className="plan-property">
              {PLAN_TYPES_MAPPING[plan.planType]}
            </div>
            <div className="plan-property plan-property-value position-relative">
              {!isEditing ? (
                <div>
                  {plan.amount}
                  {PLAN_TYPE_MAPPING[plan.planType]}
                </div>
              ) : (
                <Controller
                  name={`plans.${allSubscriptionPlans.findIndex(
                    (p) => p.id === plan.id
                  )}.amount`}
                  control={control}
                  render={({ field }) => (
                    <>
                      <input
                        {...field}
                        className="plans-update-input-field position-absolute"
                      />
                      {errors.plans?.[
                        allSubscriptionPlans.findIndex((p) => p.id === plan.id)
                      ]?.amount && (
                        <div className="error mt-4">
                          {
                            errors.plans[
                              allSubscriptionPlans.findIndex(
                                (p) => p.id === plan.id
                              )
                            ]?.amount.message
                          }
                        </div>
                      )}
                    </>
                  )}
                />
              )}
            </div>
          </div>
        ))}
        {currentTab === subscriptionTabs.CUSTOMER && (
          <>
            <div className="plan-property plan-property-heading mb-3">
              Free Trial
            </div>
            <div className="d-flex justify-content-between">
              <div className="plan-property">Number of Days</div>
              <div className="plan-property plan-property-value position-relative">
                {!isEditing ? (
                  plans[0].trialDays
                ) : (
                  <Controller
                    name={`plans.${allSubscriptionPlans.findIndex(
                      (p) => p.id === plans[0].id
                    )}.trialDays`}
                    control={control}
                    render={({ field }) => (
                      <>
                        <input
                          {...field}
                          className="plans-update-input-field position-absolute"
                          onChange={(e) => {
                            const updatedPlans = getValues("plans").map((p) =>
                              p.plan === planType
                                ? { ...p, trialDays: e.target.value }
                                : p
                            );
                            setValue("plans", updatedPlans);
                          }}
                        />
                        {errors.plans?.[
                          allSubscriptionPlans.findIndex(
                            (p) => p.id === plans[0].id
                          )
                        ]?.trialDays && (
                          <div className="error mt-4">
                            {
                              errors.plans[
                                allSubscriptionPlans.findIndex(
                                  (p) => p.id === plans[0].id
                                )
                              ]?.trialDays.message
                            }
                          </div>
                        )}
                      </>
                    )}
                  />
                )}
              </div>
            </div>
          </>
        )}
        {currentTab !== subscriptionTabs.CUSTOMER && (
          <>
            <div className="plan-property plan-property-heading mb-3">
              Listings allowed:
            </div>
            <div className="d-flex justify-content-between mb-3">
              <div className="plan-property">Free Listings</div>
              <div className="plan-property plan-property-value position-relative">
                {!isEditing ? (
                  plans[0].freeListing
                ) : (
                  <Controller
                    name={`plans.${allSubscriptionPlans.findIndex(
                      (p) => p.id === plans[0].id
                    )}.freeListing`}
                    control={control}
                    render={({ field }) => (
                      <>
                        <input
                          {...field}
                          className="plans-update-input-field position-absolute"
                          onChange={(e) => {
                            const updatedPlans = getValues("plans").map((p) =>
                              p.plan === planType
                                ? { ...p, freeListing: e.target.value }
                                : p
                            );
                            setValue("plans", updatedPlans);
                          }}
                        />
                        {errors.plans?.[
                          allSubscriptionPlans.findIndex(
                            (p) => p.id === plans[0].id
                          )
                        ]?.freeListing && (
                          <div className="error mt-4">
                            {
                              errors.plans[
                                allSubscriptionPlans.findIndex(
                                  (p) => p.id === plans[0].id
                                )
                              ]?.freeListing.message
                            }
                          </div>
                        )}
                      </>
                    )}
                  />
                )}
              </div>
            </div>
            <div className="d-flex justify-content-between">
              <div className="plan-property">Additional Listings</div>
              <div className="plan-property plan-property-value position-relative">
                {!isEditing ? (
                  plans[0].additionalListing
                ) : (
                  <Controller
                    name={`plans.${allSubscriptionPlans.findIndex(
                      (p) => p.id === plans[0].id
                    )}.additionalListing`}
                    control={control}
                    render={({ field }) => (
                      <>
                        <input
                          {...field}
                          className="plans-update-input-field position-absolute"
                          onChange={(e) => {
                            const updatedPlans = getValues("plans").map((p) =>
                              p.plan === planType
                                ? { ...p, additionalListing: e.target.value }
                                : p
                            );
                            setValue("plans", updatedPlans);
                          }}
                        />
                        {errors.plans?.[
                          allSubscriptionPlans.findIndex(
                            (p) => p.id === plans[0].id
                          )
                        ]?.additionalListing && (
                          <div className="error mt-4">
                            {
                              errors.plans[
                                allSubscriptionPlans.findIndex(
                                  (p) => p.id === plans[0].id
                                )
                              ]?.additionalListing.message
                            }
                          </div>
                        )}
                      </>
                    )}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <MainContentWrapper>
      <ConfirmationPopup
        message="Are you sure you want to update the subscriptions?"
        show={confirmationModal}
        onCancel={closeConfirmationModal}
        onConfirm={() =>
          updateAllSubscriptionPlans(
            { subscriptionPlans: getValues("plans") },
            () => {
              setIsEditing(false);
              getSubscriptionPlans(USER_ROLE.SUPER_ADMIN);
              closeConfirmationModal();
            }
          )
        }
      />
      {spinnerActions.includes(GET_SUBSCRIPTION_PLANS) && (
        <MainSpinner className="admin-spinner" />
      )}
      <div className="admin-page-heading">Subscription</div>
      <div className="row mt-4">
        <div className="col-10">
          <div className="agent-tabs mt-0">
            {Object.values(subscriptionTabs).map((tab) => (
              <div
                key={tab}
                className={`tab text-center pointer d-inline-block ${
                  currentTab === tab ? "selected-tab" : EMPTY_STRING
                }`}
                onClick={() => {
                  setCurrentTab(tab);
                  setIsEditing(false);
                }}
              >
                {tab === subscriptionTabs.CUSTOMER ? "Customer" : "Agent"}
                <div className="trapezoid" />
              </div>
            ))}
          </div>
        </div>
        <div className="col-2">
          {!isEditing && (
            <div
              className="d-flex justify-content-end gap-1 pointer mt-2 mt-sm-0"
              onClick={() => setIsEditing(true)}
            >
              <img
                src={EditCountryIcon}
                className="edit-country-info-image"
                alt="Edit"
              />
              <div className="edit-country-info-text">Edit</div>
            </div>
          )}
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="plans-container">
          <div className="row">
            {Object.keys(groupedPlans)
              .filter((key) =>
                currentTab === subscriptionTabs.CUSTOMER
                  ? groupedPlans[key][0].roleId === USER_ROLE.USER
                  : groupedPlans[key][0].roleId === USER_ROLE.AGENT
              )
              .map((key, index, filteredKeys) => (
                <React.Fragment key={key}>
                  {renderPlans(groupedPlans[key])}

                  {/* // Extra large screens: First two items */}
                  {((window.innerWidth >= 1200 && index + 1 <= 2) ||
                    // // Large/Medium screens: First item
                    (window.innerWidth >= 768 &&
                      window.innerWidth < 1200 &&
                      index === 0)) &&
                    index < filteredKeys.length - 1 && (
                      <div className="col-sm-2 col-lg-1 plans-divider mt-5">
                        <div />
                      </div>
                    )}
                </React.Fragment>
              ))}
          </div>
        </div>
        {isEditing && (
          <div className="agent-approval-buttons-container d-flex justify-content-center gap-3 mt-5 pt-5">
            <RoundedButton type="submit">Update</RoundedButton>
            <RoundedButton
              type="button"
              className="reject-button reject-button-outlined"
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </RoundedButton>
          </div>
        )}
      </form>
    </MainContentWrapper>
  );
};

const mapStateToProps = (state) => ({
  allSubscriptionPlans: state.subscriptionReducer.subscriptionPlans,
  spinnerActions: state.systemReducer.spinnerActions,
});

const mapDispatchToProps = (dispatch) => ({
  getSubscriptionPlans: (role) =>
    dispatch(subscriptionActions.getSubscriptionPlans(role)),
  updateAllSubscriptionPlans: (data, func) =>
    dispatch(subscriptionActions.updateAllSubscriptionPlans(data, func)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Subscription);
